import { Link } from 'react-router-dom';
import { ArrowRight, Users, Heart, GraduationCap, Train, ChevronRight, Vote, FileText, History, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { siteInfo, heroContent, aboutPreview, programmaPreview, elezioni2025 } from '../data/siteData';

const iconMap = {
  Users: Users,
  Heart: Heart,
  GraduationCap: GraduationCap,
  Train: Train
};

export const Home = () => {
  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        data-testid="hero-section"
      >
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${heroContent.backgroundImage})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-[#002060]/70 via-[#002060]/50 to-[#002060]/80"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 text-center">
          <div className="animate-fade-in-up">
            <img 
              src={siteInfo.logo}
              alt={siteInfo.name}
              className="w-32 h-32 md:w-40 md:h-40 mx-auto mb-8 rounded-full border-4 border-[#5CB9E5] shadow-2xl"
            />
          </div>
          
          <h1 className="font-serif text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-6 animate-fade-in-up animation-delay-100">
            {heroContent.title}
          </h1>
          
          <p className="text-xl md:text-2xl text-[#5CB9E5] font-medium mb-6 animate-fade-in-up animation-delay-200 px-4">
            {heroContent.subtitle}
          </p>
          
          <p className="text-white/90 text-base md:text-lg max-w-3xl mx-auto mb-10 leading-relaxed animate-fade-in-up animation-delay-300">
            {heroContent.description}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up animation-delay-400">
            <Link to="/unisciti">
              <Button 
                className="btn-gold px-8 py-4 text-lg rounded-lg font-semibold flex items-center gap-2"
                data-testid="hero-cta-primary"
              >
                {heroContent.cta.primary.text}
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
            <Link to={heroContent.cta.secondary.link}>
              <Button 
                variant="outline"
                className="px-8 py-4 text-lg rounded-lg font-semibold border-2 border-white text-white hover:bg-white hover:text-[#002060]"
                data-testid="hero-cta-secondary"
              >
                {heroContent.cta.secondary.text}
              </Button>
            </Link>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-8 h-12 border-2 border-white/50 rounded-full flex items-start justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/50 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-in-left">
              <span className="text-xs uppercase tracking-widest font-semibold text-[#5CB9E5] mb-4 block">
                Chi Siamo
              </span>
              <h2 className="font-serif text-3xl md:text-5xl font-bold text-[#002060] mb-6">
                Un nuovo modo di fare politica
              </h2>
              <blockquote className="border-l-4 border-[#FFD700] pl-6 py-2 mb-6">
                <p className="text-[#002060]/80 italic text-lg leading-relaxed">
                  "{aboutPreview.quote}"
                </p>
              </blockquote>
              <p className="text-slate-600 leading-relaxed mb-8">
                {aboutPreview.text}
              </p>
              <Link to="/storia">
                <Button className="btn-primary px-6 py-3 rounded-lg font-semibold flex items-center gap-2">
                  Scopri la nostra storia
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </Link>
            </div>
            <div className="animate-slide-in-right">
              <img 
                src="https://customer-assets.emergentagent.com/job_civico-evolution/artifacts/v6hj59v7_IMG_3596.jpeg"
                alt="Una parte del nostro team"
                className="w-full h-auto object-contain rounded-lg shadow-2xl"
              />
              <p className="text-center text-slate-500 text-sm mt-3 italic">
                Una parte del nostro team
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Elezioni 2025 Section */}
      <section className="py-20 md:py-28 bg-[#F4F6F8]" data-testid="elections-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#5CB9E5] mb-4 block">
              Elezioni 2025
            </span>
            <h2 className="font-serif text-3xl md:text-5xl font-bold text-[#002060] mb-4">
              Siamo pronti per il cambiamento
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Scopri i nostri candidati e il programma per le elezioni comunali e regionali 2025
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Comunali Card */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden card-hover border-l-4 border-[#5CB9E5]" data-testid="election-card-comunali">
              <div className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-[#5CB9E5]/10 rounded-lg">
                    <Vote className="w-6 h-6 text-[#5CB9E5]" />
                  </div>
                  <h3 className="font-serif text-2xl font-bold text-[#002060]">
                    Elezioni Comunali
                  </h3>
                </div>
                <p className="text-slate-600 mb-2">Consiglio Comunale di Aosta</p>
                <p className="text-[#002060] font-semibold mb-6">
                  Candidato Sindaco: {elezioni2025.comunali.candidatoSindaco.nome}
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link to="/elezioni-comunali">
                    <Button className="btn-primary px-5 py-2 rounded-lg text-sm flex items-center gap-2">
                      Scopri i candidati
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                  <a href={elezioni2025.comunali.programma} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" className="px-5 py-2 rounded-lg text-sm border-[#002060] text-[#002060] flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Programma
                    </Button>
                  </a>
                </div>
              </div>
            </div>

            {/* Regionali Card */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden card-hover border-l-4 border-[#FFD700]" data-testid="election-card-regionali">
              <div className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-[#FFD700]/10 rounded-lg">
                    <Vote className="w-6 h-6 text-[#FFD700]" />
                  </div>
                  <h3 className="font-serif text-2xl font-bold text-[#002060]">
                    Elezioni Regionali
                  </h3>
                </div>
                <p className="text-slate-600 mb-2">Consiglio Regionale Valle d'Aosta</p>
                <p className="text-[#002060] font-semibold mb-1">Lista INSIEME - ENSEMBLE</p>
                <p className="text-[#5CB9E5] text-sm mb-6">{elezioni2025.regionali.coalition}</p>
                <div className="flex flex-wrap gap-3">
                  <Link to="/elezioni-regionali">
                    <Button className="btn-gold px-5 py-2 rounded-lg text-sm flex items-center gap-2">
                      Scopri i candidati
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                  <Link to={elezioni2025.regionali.programma}>
                    <Button variant="outline" className="px-5 py-2 rounded-lg text-sm border-[#002060] text-[#002060] flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Programma
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Programma Preview Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="program-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#5CB9E5] mb-4 block">
              I Nostri Valori
            </span>
            <h2 className="font-serif text-3xl md:text-5xl font-bold text-[#002060] mb-4">
              Per una Valle d'Aosta migliore
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Crediamo in una politica che metta al centro le persone, la sanità, la formazione e lo sviluppo sostenibile
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {programmaPreview.map((item, index) => {
              const IconComponent = iconMap[item.icon];
              return (
                <div 
                  key={item.id}
                  className="bg-[#F4F6F8] rounded-xl p-6 card-hover border-l-4 border-[#5CB9E5]"
                  data-testid={`program-card-${index}`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="p-3 bg-[#002060] rounded-lg w-fit mb-4">
                    <IconComponent className="w-6 h-6 text-[#FFD700]" />
                  </div>
                  <h3 className="font-serif text-xl font-bold text-[#002060] mb-3">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/valori">
              <Button className="btn-primary px-8 py-3 rounded-lg font-semibold flex items-center gap-2 mx-auto">
                Scopri tutti i nostri valori
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-[#002060] relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#5CB9E5] rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#FFD700] rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Sparkles className="w-12 h-12 text-[#FFD700] mx-auto mb-6" />
          <h2 className="font-serif text-3xl md:text-5xl font-bold text-white mb-6">
            Entra nella squadra
          </h2>
          <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto">
            Unisciti a noi per costruire insieme il futuro della Valle d'Aosta. 
            Ogni voce conta, ogni idea è preziosa.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/unisciti">
              <Button className="btn-gold px-8 py-4 text-lg rounded-lg font-semibold flex items-center gap-2">
                Unisciti a noi
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/elezioni-trasparenti">
              <Button 
                variant="outline"
                className="px-8 py-4 text-lg rounded-lg font-semibold border-2 border-white text-white hover:bg-white hover:text-[#002060]"
              >
                Trasparenza elettorale
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
